import type { ProjectionLog } from "../../../projections/projectionEngine.js";

export type VoiceStateChip = {
    icon: string;
    label: string;
    active: boolean;
};

export type VoiceSnapshotState = {
    streaming: boolean | null;
    video: boolean | null;
    selfMute: boolean | null;
    selfDeaf: boolean | null;
    serverMute: boolean | null;
    serverDeaf: boolean | null;
};

type VoiceLogExtra = Partial<Record<keyof VoiceSnapshotState, boolean>>;

function readExtra(log: ProjectionLog): VoiceLogExtra {
    return log.extra && typeof log.extra === "object"
        ? log.extra as VoiceLogExtra
        : {};
}

export function getVoiceStateChips(log: ProjectionLog): VoiceStateChip[] {
    const extra = readExtra(log);

    if (log.type === "voice_state_stream")
        return [ {
            icon    : "screen_share",
            label   : extra.streaming ? "Stream actif" : "Stream arrêté",
            active  : Boolean(extra.streaming),
        } ];

    if (log.type === "voice_state_video")
        return [ {
            icon    : "videocam",
            label   : extra.video ? "Cam activée" : "Cam désactivée",
            active  : Boolean(extra.video),
        } ];

    if (log.type !== "voice_state_update")
        return [];

    return [
        {
            icon    : "mic_off",
            label   : extra.selfMute ? "Micro coupé" : "Micro ouvert",
            active  : Boolean(extra.selfMute),
        },
        {
            icon    : "headset_off",
            label   : extra.selfDeaf ? "Casque coupé" : "Casque actif",
            active  : Boolean(extra.selfDeaf),
        },
        {
            icon    : "admin_panel_settings",
            label   : extra.serverMute ? "Mute serveur" : "Micro non mute serveur",
            active  : Boolean(extra.serverMute),
        },
        {
            icon    : "hearing_disabled",
            label   : extra.serverDeaf ? "Sourd serveur" : "Non sourd serveur",
            active  : Boolean(extra.serverDeaf),
        },
    ];
}

export function getVoiceSnapshotStateChips(state: VoiceSnapshotState): VoiceStateChip[] {
    const chips = [
        {
            icon    : "screen_share",
            label   : "Stream",
            active  : state.streaming,
        },
        {
            icon    : "videocam",
            label   : "Cam",
            active  : state.video,
        },
        {
            icon    : "mic_off",
            label   : "Micro coupé",
            active  : state.selfMute,
        },
        {
            icon    : "headset_off",
            label   : "Casque coupé",
            active  : state.selfDeaf,
        },
        {
            icon    : "admin_panel_settings",
            label   : "Mute serveur",
            active  : state.serverMute,
        },
        {
            icon    : "hearing_disabled",
            label   : "Sourd serveur",
            active  : state.serverDeaf,
        },
    ].filter((chip): chip is VoiceStateChip => chip.active === true);
    const unknown = Object.values(state).every((value) => value === null);

    if (unknown)
        chips.push({
            icon    : "help",
            label   : "État inconnu",
            active  : false,
        });

    return chips;
}
